import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import React from 'react'

import SvgAngleRight from '@Assets/SVG/AngleRight'
import { Buy } from '@Components/BuyButton'
import { useBuyContext } from '@Components/BuyButton/hooks'
import { useSelectedAddons } from '@Domains/Product/Addons/hooks'
import { BuyQuantityProducts } from '@Domains/Product/Features/Display/Info/Info'
import { Product } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './BuyButtonArea.module.scss'

const MultiVariantBuyButton = ({
  className,
  promptsOpen,
}: {
  className?: string
  promptsOpen: boolean
}) => {
  const { t } = useTranslation()
  const isVariantB = useSplitTest('select_size_v1', 'b')

  return (
    <div className={clsx(!promptsOpen && styles.stickyBox, className)}>
      <div className={styles.box}>
        <Buy.Button className={styles.buyButton} theme="black">
          {isVariantB
            ? t('cms:ADD_TO_CART')
            : (
              <>
                {t('products:CHOOSE_SIZE')}
                <SvgAngleRight className={styles.icon} />
              </>
            )}
        </Buy.Button>
      </div>
    </div>
  )
}

export const BuyButtonAreaRoot = ({
  buyPersonalizationID,
  buyQuantitySelection,
  children,
  product,
  selectedCrossSellIDs,
  selectedVariantID,
}: {
  buyPersonalizationID?: string | null
  buyQuantitySelection: BuyQuantityProducts
  children: React.ReactNode
  product: Product
  selectedCrossSellIDs: string[]
  selectedVariantID: string
}) => {
  const addons = useSelectedAddons()

  return (
    <Buy.Root
      productID={product.id}
      products={[
        {
          productID: product.id,
          quantity: buyQuantitySelection.quantity,
          variantID: selectedVariantID,
        },
        ...(buyPersonalizationID
          ? [
            {
              productID: buyPersonalizationID,
              parentIndex: 0,
            },
          ]
          : []),
        ...(isEmpty(selectedCrossSellIDs)
          ? []
          : selectedCrossSellIDs.map(productID => ({
            productID,
            quantity: 1,
          }))),
        ...addons,
      ]}
      quantity={buyQuantitySelection.quantity}
      variantID={selectedVariantID}
    >
      {children}
    </Buy.Root>
  )
}

export const BuyButtonArea = ({
  className,
  variantsInTotal,
}: {
  className?: string
  variantsInTotal: number
}) => {
  const { t } = useTranslation()
  const { prompts } = useBuyContext()
  const promptsOpen = prompts.length !== 0

  return (
    <>
      {variantsInTotal > 1
        ? (
          <MultiVariantBuyButton className={className} promptsOpen={promptsOpen} />
        )
        : (
          <div className={clsx(!promptsOpen && styles.stickyBox, className)}>
            <div className={styles.box}>
              <Buy.Button className={styles.buyButton} theme="black">
                {t('cms:ADD_TO_CART')}
              </Buy.Button>
            </div>
          </div>
        )}
      <Buy.Prompts />
    </>
  )
}
